import React from 'react';
import { 
  Eye, Edit2, Trash2, Send, CheckCircle, Clock, 
  ChevronLeft, ChevronRight, Loader2, Receipt
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { formatCurrency, calculateTotal, formatDateDisplay } from './constants';

const PatientsTable = ({
  patients,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onToggleEnviado,
  onToggleInformeEnviado,
  onGenerateSale,
  hasRole
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a5192]" />
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        No se encontraron pacientes
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table data-testid="patients-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Fecha</TableHead>
              <TableHead className="font-semibold text-slate-700">Paciente</TableHead>
              <TableHead className="font-semibold text-slate-700">Teléfono</TableHead>
              <TableHead className="font-semibold text-slate-700">Examen</TableHead>
              <TableHead className="font-semibold text-slate-700">Médico Ref.</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Total</TableHead>
              <TableHead className="font-semibold text-slate-700 text-center">Estudios</TableHead>
              <TableHead className="font-semibold text-slate-700 text-center">Informe</TableHead>
              <TableHead className="font-semibold text-slate-700 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {patients.map((patient) => {
              const total = calculateTotal(patient.precio, patient.tipo_descuento, patient.descuento);
              return (
                <TableRow 
                  key={patient.id} 
                  className="hover:bg-slate-50 transition-colors"
                  data-testid={`patient-row-${patient.id}`}
                >
                  <TableCell className="font-mono text-sm text-slate-600">
                    {formatDateDisplay(patient.fecha)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{patient.nombre}</p>
                      <p className="text-xs text-slate-500">{patient.edad} años</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{patient.telefono}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs border-slate-300">
                      {patient.tipo_examen}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {patient.medico_referente || '-'}
                  </TableCell>
                  <TableCell className="text-right font-medium text-emerald-600">
                    {formatCurrency(total)}
                  </TableCell>
                  {/* Estado Estudios */}
                  <TableCell className="text-center">
                    <Badge 
                      className={`cursor-pointer ${
                        patient.enviado 
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                          : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      }`}
                      onClick={() => onToggleEnviado(patient)}
                      title={patient.enviado ? 'Estudios enviados - Click para cambiar' : 'Estudios pendientes - Click para enviar'}
                    >
                      {patient.enviado ? (
                        <><CheckCircle className="w-4 h-4 mr-1" />Enviado</>
                      ) : (
                        <><Clock className="w-4 h-4 mr-1" />Pendiente</>
                      )}
                    </Badge>
                  </TableCell>
                  {/* Estado Informe */}
                  <TableCell className="text-center">
                    <Badge 
                      className={`cursor-pointer ${
                        patient.informe_enviado 
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      onClick={() => onToggleInformeEnviado(patient)}
                      title={patient.informe_enviado ? 'Informe enviado - Click para cambiar' : 'Informe pendiente - Click para enviar'}
                    >
                      {patient.informe_enviado ? (
                        <><Send className="w-4 h-4 mr-1" />Enviado</>
                      ) : (
                        <><Clock className="w-4 h-4 mr-1" />Pendiente</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onView(patient)}
                        className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {onGenerateSale && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onGenerateSale(patient)}
                          className="h-8 w-8 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                          title="Generar Venta"
                          data-testid={`generate-sale-${patient.id}`}
                        >
                          <Receipt className="w-4 h-4" />
                        </Button>
                      )}
                      {hasRole(['SUPERADMIN', 'ADMIN', 'RECEPTIONIST']) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(patient)}
                          className="h-8 w-8 text-slate-500 hover:text-brand-main hover:bg-brand-main/10"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {hasRole(['SUPERADMIN', 'ADMIN']) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(patient)}
                          className="h-8 w-8 text-slate-500 hover:text-red-500 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500">
            Página {currentPage} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-slate-200"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="border-slate-200"
            >
              Siguiente
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientsTable;
